import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import React, { useEffect, useState } from "react";
import Table from "../../../ui-components/table/table";
import { InboundPickupsFilter, InboundPickupsFilters } from "./filter/filter";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "../../../app/data/apiQueryAdapter";
import { userSelector } from "../../../slices/user/userSlice";
import "./inboundPickups.scss";
import { getOutboundInboundInhouseColumns } from "./getOutboundInboundInhouseColumns";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { getPickupDetails } from "../../../services/pickups";
import { getPickup, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { PageResponseModel } from "../../../app/data/common/models";
import { PickupsResponseContentModel } from "../../../app/data/pickup/models";
import mixpanel from "mixpanel-browser";
import { useHistory } from "react-router-dom";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { getTerminalsList, terminalSummarySelector } from "slices/terminal-summary/terminalSummarySlice";
import TerminalSummaryState from "slices/terminal-summary/TerminalSummaryState";

export const InboundPickups = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userState = useSelector(userSelector);
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const [filters, setFilters] = useState<InboundPickupsFilters>({});
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");
  const [terminalOptions, setTerminalOptions] = useState<{
    value: string;
    label: string;
  }[]>([]);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "outbound_list" });
  }, [userState.activeTerminal?.id]);

  const {
    data: inboundData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    isPreviousData
  } = useInfiniteQuery(
    [
      QUERY_KEYS.OUTBOUND_PLANNING,
      {
        terminalId: userState.activeTerminal?.id,
        destination: filters.d,
        freightTypes: filters.s,
        etaDate: filters.e,
      }
    ],
    async ({ pageParam = 0 }) => {
      return await apiQueryAdapter.query<PageResponseModel<PickupsResponseContentModel>>("/pickups/inbound", "GET", {
        params: {
          page: pageParam,
          originTerminalCode: userState.activeTerminal?.id,
          destinationTerminalCode: filters.d,
          freightTypes: !!filters.s ? filters.s.join(",") : null,
          etaDate: filters?.e
        },
      });
    },
    {
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) =>
        !lastPage.data.last ? lastPage.data.pageable.pageNumber + 1 : null,
      keepPreviousData: true,
      enabled: !!userState.activeTerminal?.id,
    }
  );

  const onSearch = (newFilters: any) => {
    setPickupDetailsNumber("");
    setFilters(newFilters);
  };

  const onShowPickupDetails = (pickupNumber: string) => {
    if (pickupDetailsNumber !== pickupNumber) {
      setPickupDetailsNumber(pickupNumber);
      dispatch(getPickup(pickupNumber));
    }
  };

  const pickupDetails = pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);

  const tableData =
    inboundData?.pages.flatMap((page) => page.data.content) || [];

  const terminalAvailable = !!userState.activeTerminal?.id;

  const handlePickupClick = (pickup: string) => {
    history.push(`/pickup/management?p=${pickup}`);
  }

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
        value: terminal.terminalNumber.toString(),
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  return (
    <ContentContainer 
      mods={{ "full-width": true }} 
      title="Outbound Planning List"
      tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
      status={"PREVIEW"}
    >
      {(!terminalAvailable) && (
        <XGSErrorMessage>
          <div>
            You do not have assigned terminals.
            Email <a className="white-link" href="mailto:helpdesk@xgsi.com">helpdesk@xgsi.com</a> to request terminal assignment for your account.
          </div>
        </XGSErrorMessage>
      )}
      {isError && <XGSErrorMessage>{error}</XGSErrorMessage>}
      {(!isError && terminalAvailable) && (
        <div className="xgs-inbound-pickups">
          <InboundPickupsFilter isLoading={isFetching} onSearch={onSearch} terminalOptions={terminalOptions}/>
          <div className="xgs-inbound-pickups__table">
            <Table
              columns={getOutboundInboundInhouseColumns(handlePickupClick)}
              data={tableData}
              isLoading={isFetching || isPreviousData}
              infiniteScroll
              cursorPointer
              onInfiniteScroll={() => fetchNextPage()}
              infiniteScrollHasNext={!inboundData?.pages.some(
                (page) => page.data.content.length !== 50
              )}
              keepTableOnLoading
              infiniteScrollLoading={isFetchingNextPage}
              rowHeight={56}
              responsive={false}
              minTableHeight={320}
              onRowClicked={(row: any) =>
                onShowPickupDetails(row.pickup?.pickupNumber)
              }
            />
          </div>
        </div>
      )}
      <PickupDetails
        readonly
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={() => setPickupDetailsNumber("")}
        isLoading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GET_PICKUP"}
        error={pickupDetailsState.requestCreator === "GET_PICKUP" ? pickupDetailsState.requestError : ""}
      />
    </ContentContainer>
  );
};
